/**
 * Rich text of the Text widget: a small, safe subset of HTML.
 *
 * Authors format their text (bold, italic, underline, strike, lists, links,
 * a few theme colours); viewers — clients included — read it. Whatever is
 * stored, it is rendered only after `sanitizeRichText`: an allowlist of tags,
 * no attribute but a link's address and a colour name, no script, style or
 * event handler. Colours are theme roles, never raw values, so the text stays
 * readable in light and dark themes.
 */

export const TEXT_COLORS = [
  { value: "muted", label: "Muted" },
  { value: "accent", label: "Accent" },
  { value: "success", label: "Success" },
  { value: "warning", label: "Warning" },
  { value: "critical", label: "Critical" },
] as const;

export type TextColor = (typeof TEXT_COLORS)[number]["value"];

const COLOR_NAMES = new Set<string>(TEXT_COLORS.map((color) => color.value));

/** Tags kept as they are. `b`/`i` are normalized to `strong`/`em`. */
const ALLOWED_TAGS = new Set([
  "p",
  "div",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "span",
  "ul",
  "ol",
  "li",
  "a",
]);

const RENAMED_TAGS: Record<string, string> = { b: "strong", i: "em", strike: "s", del: "s" };

/** Elements removed with everything they contain. */
const DROPPED_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "template",
  "head",
  "title",
  "svg",
  "math",
]);

function safeHref(value: string | null): string | null {
  if (!value) return null;
  const href = value.trim();
  return /^(https?:|mailto:)/i.test(href) ? href : null;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function cleanChildren(source: Node, target: Node, doc: Document) {
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.textContent ?? ""));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const element = child as Element;
    const name = element.tagName.toLowerCase();
    if (DROPPED_TAGS.has(name)) return;
    const tag = RENAMED_TAGS[name] ?? name;
    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown wrappers (font, h1, table…) keep their text, not their markup.
      cleanChildren(element, target, doc);
      return;
    }
    const clean = doc.createElement(tag);
    if (tag === "a") {
      const href = safeHref(element.getAttribute("href"));
      if (href) {
        clean.setAttribute("href", href);
        clean.setAttribute("target", "_blank");
        clean.setAttribute("rel", "noopener noreferrer");
      }
    }
    const color = element.getAttribute("data-color");
    if (color && COLOR_NAMES.has(color)) clean.setAttribute("data-color", color);
    cleanChildren(element, clean, doc);
    // A span without a colour carries nothing: keep only its content.
    if (tag === "span" && !clean.hasAttribute("data-color")) {
      while (clean.firstChild) target.appendChild(clean.firstChild);
      return;
    }
    target.appendChild(clean);
  });
}

/** The safe subset of `html`; plain text is accepted too. */
export function sanitizeRichText(html: string | undefined | null): string {
  if (!html) return "";
  if (typeof DOMParser === "undefined") return escapeHtml(html);
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const out = doc.createElement("div");
  cleanChildren(doc.body, out, doc);
  return out.innerHTML.trim();
}

/** Plain text written before rich text existed, as paragraphs. */
export function plainTextToRichText(text: string | undefined | null): string {
  if (!text?.trim()) return "";
  return text
    .trim()
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/** Whether the rich text has anything to read. */
export function hasRichText(html: string): boolean {
  return (
    html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim().length > 0
  );
}
